<template>
  <div class="teamActivity">
    <!-- ── Filters ─────────────────────────────────────────────── -->
    <div class="filterBar">
      <SearchSelect
        class="filterPicker"
        :modelValue="datePreset"
        @update:modelValue="datePreset = $event"
        :options="datePresetOptions"
        :hideSearch="true"
        :allowClear="false"
        placeholder="Date range"
      />

      <template v-if="datePreset === 'custom'">
        <input type="date" v-model="customStart" class="filterInput" />
        <span class="filterSep">&ndash;</span>
        <input type="date" v-model="customEnd" class="filterInput" />
      </template>

      <SearchSelect
        class="filterPicker"
        :modelValue="salesPlayId"
        @update:modelValue="salesPlayId = $event"
        :options="salesPlayOptions"
        :hideSearch="true"
        :allowClear="true"
        clearLabel="All sales plays"
        placeholder="All sales plays"
      />
    </div>

    <!-- ── Loading / error states ──────────────────────────────── -->
    <div v-if="loading" class="statusMsg">Loading report&hellip;</div>
    <div v-else-if="error" class="statusMsg error">{{ error }}</div>

    <template v-else-if="data">
      <!-- ── Summary stats row ───────────────────────────────────── -->
      <div class="summaryRow">
        <div class="statCard">
          <span class="statValue">{{ fmt(data.totals.prospects_added) }}</span>
          <span class="statLabel">Prospects added</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.totals.active_prospects) }}</span>
          <span class="statLabel">Active prospects</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.totals.outbound_calls) }}</span>
          <span class="statLabel">Outbound calls</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.totals.emails_delivered) }}</span>
          <span class="statLabel">Emails delivered</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.totals.voicemails_left) }}</span>
          <span class="statLabel">Voicemails left</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.totals.li_connects) }}</span>
          <span class="statLabel">LI connects</span>
        </div>
      </div>

      <!-- ── Team table ──────────────────────────────────────────── -->
      <div class="tableWrap">
        <table class="activityTable">
          <thead>
            <tr>
              <th class="colName">Name</th>
              <th class="colNum">Prospects added</th>
              <th class="colNum">Active prospects</th>
              <th class="colNum">Outbound calls</th>
              <th class="colNum">Emails delivered</th>
              <th class="colNum">Voicemails left</th>
              <th class="colNum">LI connects</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in data.members" :key="m.user_id ?? 'unassigned'">
              <td class="colName">{{ displayName(m) }}</td>
              <td class="colNum">{{ fmt(m.prospects_added) }}</td>
              <td class="colNum">{{ fmt(m.active_prospects) }}</td>
              <td class="colNum">{{ fmt(m.outbound_calls) }}</td>
              <td class="colNum">{{ fmt(m.emails_delivered) }}</td>
              <td class="colNum">{{ fmt(m.voicemails_left) }}</td>
              <td class="colNum">{{ fmt(m.li_connects) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="data.members.length === 0" class="emptyMsg">
        No activity found for the selected period.
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, watch } from 'vue'

import SearchSelect from '@/components/SearchSelect.vue'
import { datePresetOptions, REPORTS_FILTER_KEY } from '../../composables/useReportsFilters'
import { useTeamActivity, type TeamMemberStats } from '../../composables/useTeamActivity'

// ── Shared filters (via provide/inject from ReportsPage) ────────────
const { datePreset, customStart, customEnd, dateRange, salesPlayId, salesPlayOptions } = inject(REPORTS_FILTER_KEY)!

// ── Data ────────────────────────────────────────────────────────────
const { data, loading, error, fetch: fetchData } = useTeamActivity()

function reload() {
  fetchData({
    startDate: dateRange.value.startDate,
    endDate: dateRange.value.endDate,
    salesPlayId: salesPlayId.value || null,
  })
}

watch([dateRange, salesPlayId], reload)

onMounted(reload)

// ── Helpers ─────────────────────────────────────────────────────────
function displayName(m: TeamMemberStats): string {
  if (!m.user_id) return 'Unassigned'
  const parts = [m.user_first_name, m.user_last_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : (m.user_email ?? 'Unknown')
}

function fmt(n: number): string {
  return n.toLocaleString()
}
</script>

<style scoped>
.teamActivity {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* ── Filters ─────────────────────────────────────────────────────── */
.filterBar {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.filterPicker { width: 180px; }
.filterInput {
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--color-border, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #fff);
  color: var(--color-text-primary);
  outline: none;
}
.filterInput:focus { border-color: var(--color-accent, #059669); }
.filterSep { color: var(--color-text-tertiary); font-size: 13px; }

/* ── Status messages ─────────────────────────────────────────────── */
.statusMsg {
  padding: var(--space-5);
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}
.statusMsg.error {
  color: var(--color-danger, #dc2626);
}

/* ── Summary row ─────────────────────────────────────────────────── */
.summaryRow {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.statCard {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: var(--space-4) var(--space-5);
  min-width: 130px;
  flex: 1;
  border: 1px solid var(--color-border-subtle, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-surface-subtle, #faf9f7);
}

.statValue {
  font-size: 28px;
  font-weight: var(--fw-semibold);
  color: var(--color-accent, #059669);
  line-height: 1.2;
}

.statLabel {
  font-size: 13px;
  font-weight: var(--fw-medium);
  color: var(--color-text-tertiary);
}

/* ── Table ────────────────────────────────────────────────────────── */
.tableWrap {
  overflow-x: auto;
}

.activityTable {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-sm);
}

.activityTable th {
  text-align: left;
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  padding: var(--space-3) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
  white-space: nowrap;
}

.activityTable td {
  padding: var(--space-4) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle, #f0ede8);
  color: var(--color-text-primary);
}

.activityTable tbody tr:hover {
  background: var(--color-surface-subtle, #faf9f7);
}

.colNum {
  text-align: right !important;
}

.colName {
  min-width: 160px;
}

.emptyMsg {
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
  padding: var(--space-7) 0;
}
</style>
